import type {
  ChangeEvent,
  FocusEventHandler,
  KeyboardEventHandler,
} from "react";
import { useState } from "react";
import { Text } from "src/components/Text/Text.tsx";
import { useControlled } from "@exonn/hooks";
import { useTranslation } from "react-i18next";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { MiniIconButton } from "src/components/MiniIconButton/MiniIconButton.tsx";

interface PaginationV2Props {
  count: number;
  page: number;
  onChange(page: number): void;
  minPage?: number;
  disabled?: boolean;
}

import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

await loadTranslations(TranslationNamespaceEnum.PaginationV2Component);

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(value, min));
};

export const PaginationV2 = (props: PaginationV2Props) => {
  const {
    count = 1,
    page: pageProp = 1,
    onChange,
    minPage = 1,
    disabled = false,
  } = props;

  const { t } = useTranslation();

  const [editMode, setEditMode] = useState(false);
  const [draftPage, setDraftPage] = useState(pageProp);

  const [page, setPage] = useControlled({
    controlled: pageProp,
    default: pageProp,
    state: "page",
    name: "Pagination",
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement> & { target: { value: string } },
  ) => {
    const numberOrEmptyRegExp = /^\d*$/;
    const numberOrString = e.target?.value?.trim();

    // allow only integers or empty string to be entered
    if (!numberOrEmptyRegExp.test(numberOrString)) return;

    setDraftPage(Number(numberOrString) ?? page);
  };

  const handleInputKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    // we are handling only Enter key
    if (e.key != "Enter") return;

    e.currentTarget.blur();
  };

  const handleInputFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setEditMode(true);
    setDraftPage(page);
    e.currentTarget.select();
  };

  const handleInputBlur: FocusEventHandler = () => {
    setEditMode(false);
    goToPage(draftPage);
  };

  const goToPage = (nextPage: number) => {
    nextPage = clamp(nextPage, minPage, count);

    // avoid duplicate setting input page if it's already ok
    if (page != nextPage) {
      setPage(nextPage);
      onChange(nextPage);
    }
  };

  const prevButtonDisabled = page <= minPage;
  const nextButtonDisabled = page >= count;

  return (
    <div css={wrapperStyles}>
      <div css={innerStyles({ disabled })}>
        <MiniIconButton
          disabled={prevButtonDisabled}
          onClick={() => goToPage(page - 1)}
        >
          <i className={"icon-15-arrw_l"} />
        </MiniIconButton>

        <Text>
          {t("page", {
            ns: TranslationNamespaceEnum.PaginationV2Component,
          })}
        </Text>

        <div css={inputWrapperStyles}>
          <input
            css={inputStyles}
            onChange={handleInputChange}
            onKeyUp={handleInputKeyUp}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            value={editMode ? draftPage : page}
            type="text"
            inputMode="numeric"
          />
        </div>

        <Text>
          {t("of", {
            count: count,
            ns: TranslationNamespaceEnum.PaginationV2Component,
          })}
        </Text>

        <MiniIconButton
          disabled={nextButtonDisabled}
          onClick={() => goToPage(page + 1)}
        >
          <i className={"icon-15-arrw_r"} />
        </MiniIconButton>
      </div>
    </div>
  );
};

const wrapperStyles = css`
  display: flex;
  align-items: center;
`;

const innerStyles =
  ({ disabled }: { disabled?: boolean }) =>
  ({ colorsV2, fonts, fontWeights, text }: ExactTheme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: transparent;
    user-select: none;
    gap: 10px;
    width: fit-content;
    line-height: 165%;
    font-family: ${fonts.normal};
    font-size: 14px;
    font-weight: ${fontWeights.normal};
    color: ${colorsV2.grey[800]};

    ${disabled && {
      opacity: 0.4,
      pointerEvents: "none",
    }});
  `;

const outline = ({
  hasError,
  theme: { inputBorderV2, colorsV2 },
  disabled = false,
}: {
  hasError: boolean;
  disabled?: boolean;
  theme: ExactTheme;
}) => ({
  boxShadow: "none",
  border:
    inputBorderV2.solid[disabled ? "disabled" : "standard"][
      hasError ? "error" : "default"
    ],

  "&:hover": {
    border: inputBorderV2.solid.hover[hasError ? "error" : "default"],
  },
  "&:focus": {
    background: "white",
    border: inputBorderV2.solid.focused[hasError ? "error" : "default"],
    outline: "none",
    boxShadow: hasError
      ? `0 0 0 1px ${colorsV2.red[500]}`
      : `0 0 0 1px ${colorsV2.blue[500]}`,
  },
});

const inputStyles = (theme: ExactTheme) => css`
  width: 33px;
  height: 34px;
  appearance: none;

  border-radius: 6px;
  padding: 0;
  background-color: transparent;
  text-align: center;
  ${outline({
    hasError: false,
    theme,
    disabled: false,
  })};
`;

const inputWrapperStyles = ({ colorsV2, borderRadiusV2 }: ExactTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 33px;
  height: 34px;
  margin: 0 -5px;
  border-width: 1px;
  border-style: solid;
  border-radius: ${borderRadiusV2.primary};
  border-color: ${colorsV2.border.muted};
  background-color: ${colorsV2.white};
  transition: border 0.25s;

  :hover {
    border-color: ${colorsV2.grey[200]};
  }
`;
